import React, { useMemo, useState } from "react";
import { Switch } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  FieldLabel,
  MoneyInput,
  SectionSaveReset,
  SectionSaveResetProps,
  TextInput,
  WholeNumberInput,
  modelOffRedSwitchSx,
  settingsDangerButtonClassName,
  settingsPrimaryButtonClassName,
  settingsReadOnlyInputClassName,
  settingsSectionCardClassName,
  settingsSectionDescriptionClassName,
  settingsSectionTitleClassName,
  settingsTableHeaderClassName,
  settingsTextareaClassName,
  switchSx,
} from "../common";
import {
  ChamberSettingsFormState,
  AccountAllocation,
  MemberBenefit,
  MembershipAdditionalCost,
  MembershipEmployeeRange,
  MembershipModel,
  PaymentSchedule,
  defaultProrationMonths,
  membershipAdditionalCosts,
} from "../settingsData";
import {
  getDerivedMonthlyMembershipPrice,
  getMembershipEmployeeRangeCode,
  isAutoMembershipEmployeeRangeCode,
  isFinalMembershipEmployeeRange,
  normalizeFinalMembershipEmployeeRange,
  normalizeFormerFinalMembershipEmployeeRange,
  normalizeMembershipModelsForForm,
} from "../settingsMappers";

type RangeField = "from" | "to" | "annualPrice";
type MembershipSettings = ChamberSettingsFormState["membership"];

type MembershipTabProps = {
  settings: MembershipSettings;
  accountAllocations: AccountAllocation[];
  onChange: (settings: MembershipSettings) => void;
  sectionControls: SectionSaveResetProps;
};

type MembershipTypeModalState = {
  mode: "add" | "edit";
  model: MembershipModel;
};

type AddOnModalState = {
  mode: "add" | "editFee";
  addOn: MembershipAdditionalCost;
};

const paymentScheduleOptions: {
  id: PaymentSchedule;
  label: string;
}[] = [
  { id: "annual", label: "Annually" },
  { id: "monthly", label: "Monthly" },
];

const tierModelIdPattern = /^tier-[1-6]$/;

const primaryButtonClassName = settingsPrimaryButtonClassName;
const dangerButtonClassName = settingsDangerButtonClassName;
const compactInputClassName = settingsReadOnlyInputClassName;
const textareaClassName = settingsTextareaClassName;
const tierBenefitsGridClassName =
  "grid grid-cols-[minmax(360px,1fr)_repeat(6,76px)_100px]";
const membershipSetupCardClassName = settingsSectionCardClassName;
const membershipSetupHeaderClassName = settingsSectionTitleClassName;
const membershipSetupDescriptionClassName = settingsSectionDescriptionClassName;
const membershipTableRowClassName =
  "grid gap-4 px-5 py-5";
const membershipTableHeaderClassName = `${settingsTableHeaderClassName} gap-4`;
const membershipGlobalInputClassName =
  "h-11 w-24 rounded-lg border border-[#DCE4F0] bg-white px-3 text-base text-[#3F4A5F] outline-none transition focus:border-cpaGold focus:ring-2 focus:ring-amber-100 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:text-gray-400";
const disabledModelSwitchSx = modelOffRedSwitchSx;

const membershipBenefitsDescription =
  "Create and manage your membership benefits that will be displayed on your chamber website under Membership Benefits. For tiered membership models you can select which benefits are available under each tier by simply checking the box next to the benefit available for each membership model tier. For non-tiered membership models check Show all benefits.";

const defaultCompanyRanges: Array<
  Pick<MembershipEmployeeRange, "id" | "code" | "from" | "to">
> = [
  { id: "company-1", code: "CM2-4", from: "2", to: "4" },
  { id: "company-2", code: "CM5-10", from: "5", to: "10" },
  { id: "company-3", code: "CM11-25", from: "11", to: "25" },
  { id: "company-4", code: "CM26-50", from: "26", to: "50" },
  { id: "company-5", code: "CM51-100", from: "51", to: "100" },
  { id: "company-6", code: "CM101-200", from: "101", to: "200" },
  { id: "company-7", code: "CM201-300", from: "201", to: "300" },
  { id: "company-8", code: "CM301-500", from: "301", to: "500" },
  { id: "company-9", code: "CM500+", from: "500", to: "" },
];

const getStableTimestampId = (prefix: string) =>
  `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

const isTierModel = (membershipModel: MembershipModel) =>
  tierModelIdPattern.test(membershipModel.id);

const parseDollars = (value: string) => {
  const parsedValue = parseFloat(value.replace(/[^0-9.-]/g, ""));
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const formatDollars = (value: number) => `$${value.toFixed(2)}`;

const getFinalRangeInputValue = (range: MembershipEmployeeRange) =>
  range.from.trim() ? `${range.from}+` : "";

const normalizeMembershipModelPositions = (
  membershipModels: MembershipModel[]
) =>
  membershipModels.map((membershipModel, index) => ({
    ...membershipModel,
    position:
      Number.isFinite(membershipModel.position) && membershipModel.position > 0
        ? membershipModel.position
        : index + 1,
  }));

const getProratedFallbackPrice = (annualPrice: string, monthIndex: number) => {
  const annualAmount = parseDollars(annualPrice);
  return formatDollars((annualAmount * (12 - monthIndex)) / 12);
};

const getProratedInputValue = ({
  annualPrice,
  monthIndex,
  savedPrice,
}: {
  annualPrice: string;
  monthIndex: number;
  savedPrice?: string;
}) => {
  const fallbackPrice = getProratedFallbackPrice(annualPrice, monthIndex);

  if (!savedPrice) {
    return fallbackPrice;
  }

  return savedPrice;
};

const createNewRange = (
  membershipModelId: string,
  index: number
): MembershipEmployeeRange => ({
  id: `${membershipModelId}-range-${Date.now()}-${index}`,
  code: "",
  from: "",
  to: "",
  annualPrice: "$0.00",
  monthlyPrice: "$0.00",
});

const createCompanyMembershipModel = (): MembershipModel => ({
  id: "company",
  code: "CM",
  name: "Company Membership",
  description: "For companies based on number of employees.",
  memberTarget: 0,
  enabled: true,
  publicMembershipApplicationEnabled: true,
  position: 1,
  paymentResponsibility: "Member/applicant pays",
  thirdPartyPayerName: "",
  thirdPartyPaymentNotes: "",
  pricingModel: "employeeRange",
  annualPrice: "$0.00",
  monthlyPrice: "$0.00",
  includedBenefitIds: [],
  additionalCosts: membershipAdditionalCosts.map((addOn) => ({ ...addOn })),
  ranges: defaultCompanyRanges.map((range) => ({
    ...range,
    annualPrice: "$0.00",
    monthlyPrice: "$0.00",
  })),
});

const createTierMembershipModel = (tierNumber: number): MembershipModel => ({
  id: `tier-${tierNumber}`,
  code: `Tier ${tierNumber}`,
  name: "Membership Tier Name",
  description: "Membership Tier Description",
  memberTarget: 0,
  enabled: tierNumber <= 3,
  publicMembershipApplicationEnabled: tierNumber <= 3,
  position: tierNumber,
  paymentResponsibility: "Member/applicant pays",
  thirdPartyPayerName: "",
  thirdPartyPaymentNotes: "",
  pricingModel: "fixed",
  annualPrice: "$0.00",
  monthlyPrice: "$0.00",
  includedBenefitIds: [],
  additionalCosts: [],
  ranges: [],
});

const createNewBenefit = (): MemberBenefit => ({
  id: getStableTimestampId("benefit"),
  label: "",
});

const createNewAddOn = (): MembershipAdditionalCost => ({
  id: getStableTimestampId("add-on"),
  code: "",
  name: "",
  enabled: true,
  annualAmount: "$0.00",
  monthlyAmount: "$0.00",
  accountNumber: "",
  description: "",
});

const ToggleStatus = ({ enabled }: { enabled: boolean }) => (
  <span
    className={`text-xs font-bold uppercase ${
      enabled ? "text-emerald-600" : "text-gray-500"
    }`}
  >
    {enabled ? "Enabled" : "Disabled"}
  </span>
);

const MembershipGlobalStepNumber = ({ value }: { value: number }) => (
  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cpaGold text-sm font-bold leading-none text-white">
    {value}
  </span>
);

const MembershipGlobalToggleStatus = ({ enabled }: { enabled: boolean }) => (
  <span
    className={`text-xs font-bold uppercase leading-none ${
      enabled ? "text-emerald-600" : "text-[#777777]"
    }`}
  >
    {enabled ? "Enabled" : "Disabled"}
  </span>
);

const getTierNumber = (membershipModel: MembershipModel) =>
  Number(membershipModel.id.replace("tier-", ""));

const isCompanyMembershipModel = (membershipModel: MembershipModel) =>
  membershipModel.id === "company" ||
  membershipModel.pricingModel === "employeeRange";

const MembershipTab = ({
  settings,
  accountAllocations,
  onChange,
  sectionControls,
}: MembershipTabProps) => {
  const [membershipTypeModal, setMembershipTypeModal] =
    useState<MembershipTypeModalState | null>(null);
  const [membershipTypeModalError, setMembershipTypeModalError] = useState("");
  const [benefitDeleteCandidateId, setBenefitDeleteCandidateId] = useState<
    string | null
  >(null);
  const [addOnModal, setAddOnModal] = useState<AddOnModalState | null>(null);

  const {
    enabledPaymentSchedules,
    partTimeEmployeesPerFullTime,
    memberBenefits,
    membershipModels,
    proratedPrices,
    globalToggleState,
    paymentBeforeApprovalRule,
  } = settings;

  const enabledScheduleCount = paymentScheduleOptions.filter(
    (schedule) => enabledPaymentSchedules[schedule.id]
  ).length;

  const companyMembershipModel = useMemo(
    () =>
      membershipModels.find((model) => model.id === "company") ||
      membershipModels.find((model) => model.pricingModel === "employeeRange"),
    [membershipModels]
  );

  const fixedMembershipModels = useMemo(
    () =>
      membershipModels.filter(
        (model) => model.pricingModel === "fixed" && !isTierModel(model)
      ),
    [membershipModels]
  );

  const enabledFixedMembershipModels = useMemo(
    () =>
      fixedMembershipModels.filter(
        (model) => model.enabled && model.publicMembershipApplicationEnabled
      ),
    [fixedMembershipModels]
  );

  const actualTierMembershipModels = useMemo(
    () => membershipModels.filter(isTierModel),
    [membershipModels]
  );

  const tierMembershipModels = useMemo(() => {
    const existingTierModelsById = new Map(
      actualTierMembershipModels.map((model) => [model.id, model] as const)
    );

    return Array.from({ length: 6 }, (_, index) => {
      const tierNumber = index + 1;
      return (
        existingTierModelsById.get(`tier-${tierNumber}`) ||
        createTierMembershipModel(tierNumber)
      );
    });
  }, [actualTierMembershipModels]);

  const enabledTierMembershipModels = useMemo(
    () =>
      tierMembershipModels.filter(
        (model) => model.enabled && model.publicMembershipApplicationEnabled
      ),
    [tierMembershipModels]
  );

  const enabledNonTierModelExists = useMemo(
    () => membershipModels.some((model) => !isTierModel(model) && model.enabled),
    [membershipModels]
  );
  const tierSetupActive =
    actualTierMembershipModels.some((model) => model.enabled) ||
    (actualTierMembershipModels.length > 0 && !enabledNonTierModelExists);

  const addOns = useMemo(() => {
    const modelWithAddOns =
      membershipModels.find(
        (model) => !isTierModel(model) && model.additionalCosts.length > 0
      ) ||
      membershipModels.find((model) => model.additionalCosts.length > 0);
    return modelWithAddOns?.additionalCosts || [];
  }, [membershipModels]);

  const accountNumberOptions = useMemo(
    () =>
      Array.from(
        new Set(
          [
            ...accountAllocations.map((account) => account.accountNumber),
            ...addOns.map((addOn) => addOn.accountNumber),
            "4000",
          ].filter((accountNumber) => accountNumber.trim())
        )
      ),
    [accountAllocations, addOns]
  );

  const setMembershipModels = (
    valueOrUpdater:
      | MembershipModel[]
      | ((current: MembershipModel[]) => MembershipModel[])
  ) => {
    onChange({
      ...settings,
      membershipModels: normalizeMembershipModelsForForm(
        normalizeMembershipModelPositions(
          typeof valueOrUpdater === "function"
            ? valueOrUpdater(membershipModels)
            : valueOrUpdater
        )
      ),
    });
  };

  const activateSizeBasedSetup = () => {
    setMembershipModels((current) => {
      const currentWithCompany = current.some(isCompanyMembershipModel)
        ? current
        : [createCompanyMembershipModel(), ...current];
      let companyModelWasEnabled = false;

      return currentWithCompany.map((membershipModel) => {
        if (isTierModel(membershipModel)) {
          return {
            ...membershipModel,
            enabled: false,
            publicMembershipApplicationEnabled: false,
          };
        }

        if (isCompanyMembershipModel(membershipModel) && !companyModelWasEnabled) {
          companyModelWasEnabled = true;
          return {
            ...membershipModel,
            enabled: true,
            publicMembershipApplicationEnabled: true,
          };
        }

        return membershipModel;
      });
    });
  };

  const activateTierBasedSetup = () => {
    setMembershipModels((current) => {
      const existingTierModelIds = new Set(
        current.filter(isTierModel).map((model) => model.id)
      );
      const missingTierModels = Array.from({ length: 6 }, (_, index) =>
        createTierMembershipModel(index + 1)
      ).filter((tierModel) => !existingTierModelIds.has(tierModel.id));
      const nextModels = [...current, ...missingTierModels];

      return nextModels.map((membershipModel) =>
        isTierModel(membershipModel)
          ? {
              ...membershipModel,
              enabled: getTierNumber(membershipModel) <= 3,
              publicMembershipApplicationEnabled:
                getTierNumber(membershipModel) <= 3,
            }
          : {
              ...membershipModel,
              enabled: false,
              publicMembershipApplicationEnabled: false,
            }
      );
    });
  };

  const updateMembershipModel = (
    id: string,
    updater: (membershipModel: MembershipModel) => MembershipModel
  ) => {
    setMembershipModels((current) =>
      current.map((membershipModel) =>
        membershipModel.id === id ? updater(membershipModel) : membershipModel
      )
    );
  };

  const updatePaymentScheduleEnabled = (
    scheduleId: PaymentSchedule,
    enabled: boolean
  ) => {
    if (
      !enabled &&
      enabledPaymentSchedules[scheduleId] &&
      enabledScheduleCount === 1
    ) {
      return;
    }

    const nextSchedules = {
      ...enabledPaymentSchedules,
      [scheduleId]: enabled,
    };

    onChange({
      ...settings,
      enabledPaymentSchedules: nextSchedules,
      globalToggleState: {
        ...globalToggleState,
        prorate: nextSchedules.annual ? globalToggleState.prorate : false,
      },
    });
  };

  const updateGlobalToggleState = (
    updater: (
      currentToggleState: MembershipSettings["globalToggleState"]
    ) => MembershipSettings["globalToggleState"]
  ) => {
    onChange({
      ...settings,
      globalToggleState: updater(globalToggleState),
    });
  };

  const updateRange = (
    membershipModelId: string,
    rangeId: string,
    field: RangeField,
    value: string
  ) => {
    updateMembershipModel(membershipModelId, (membershipModel) => ({
      ...membershipModel,
      ranges: membershipModel.ranges.map((range, index, ranges) => {
        if (range.id !== rangeId) {
          return range;
        }

        const finalRange = isFinalMembershipEmployeeRange(ranges, index);
        const nextRange = {
          ...range,
          [field]: value,
          ...(finalRange && field === "from" ? { to: "" } : {}),
        };

        return field === "from" || field === "to"
          ? {
              ...nextRange,
              code: isAutoMembershipEmployeeRangeCode(range)
                ? getMembershipEmployeeRangeCode(
                    {
                      from: nextRange.from,
                      to: finalRange ? "" : nextRange.to,
                    },
                    { openEnded: finalRange }
                  )
                : range.code,
            }
          : nextRange;
      }),
    }));
  };

  const addRange = (membershipModelId: string) => {
    updateMembershipModel(membershipModelId, (membershipModel) => ({
      ...membershipModel,
      ranges: [
        ...membershipModel.ranges.map((range, index, ranges) =>
          isFinalMembershipEmployeeRange(ranges, index)
            ? normalizeFormerFinalMembershipEmployeeRange(range)
            : range
        ),
        createNewRange(membershipModelId, membershipModel.ranges.length),
      ],
    }));
  };

  const deleteRange = (membershipModelId: string, rangeId: string) => {
    updateMembershipModel(membershipModelId, (membershipModel) => ({
      ...membershipModel,
      ranges:
        membershipModel.ranges.length > 1
          ? membershipModel.ranges
              .filter((range) => range.id !== rangeId)
              .map((range, index, ranges) =>
                isFinalMembershipEmployeeRange(ranges, index)
                  ? normalizeFinalMembershipEmployeeRange(range)
                  : range
              )
          : membershipModel.ranges,
    }));
  };

  const updateProratedPrice = (
    pricingKey: string,
    month: string,
    value: string
  ) => {
    onChange({
      ...settings,
      proratedPrices: {
        ...proratedPrices,
        [pricingKey]: {
          ...proratedPrices[pricingKey],
          [month]: value,
        },
      },
    });
  };

  const createCompanyModelIfNeeded = () => {
    if (companyMembershipModel) {
      return;
    }

    setMembershipModels((current) => [
      createCompanyMembershipModel(),
      ...current,
    ]);
  };

  const applySharedAddOns = (nextAddOns: MembershipAdditionalCost[]) => {
    setMembershipModels((current) =>
      current.map((membershipModel) => ({
        ...membershipModel,
        additionalCosts: nextAddOns,
      }))
    );
  };

  const saveMembershipTypeModal = () => {
    if (!membershipTypeModal) {
      return;
    }

    const trimmedName = membershipTypeModal.model.name.trim();
    const trimmedCode = membershipTypeModal.model.code.trim();
    const trimmedDescription = membershipTypeModal.model.description.trim();
    const isTier = isTierModel(membershipTypeModal.model);
    if (
      !trimmedName ||
      !trimmedCode ||
      (!isTier && !trimmedDescription)
    ) {
      setMembershipTypeModalError(
        isTier
          ? "Enter a code and membership tier name."
          : "Enter a unique code, membership type name, and description."
      );
      return;
    }

    const duplicateCode = membershipModels.some(
      (model) =>
        model.id !== membershipTypeModal.model.id &&
        model.code.trim().toLowerCase() === trimmedCode.toLowerCase()
    );
    if (duplicateCode) {
      setMembershipTypeModalError(
        `The membership code ${trimmedCode} is already in use.`
      );
      return;
    }

    const nextModel = {
      ...membershipTypeModal.model,
      code: trimmedCode,
      name: trimmedName,
      description: trimmedDescription,
      memberTarget: Math.max(0, membershipTypeModal.model.memberTarget || 0),
    };

    // Upsert: chamber-created membership types (CS-008) do not exist in the
    // list yet when the modal saves, so append instead of no-op mapping.
    setMembershipModels((current) =>
      current.some((model) => model.id === nextModel.id)
        ? current.map((model) =>
            model.id === nextModel.id ? nextModel : model
          )
        : [...current, nextModel]
    );

    setMembershipTypeModalError("");
    setMembershipTypeModal(null);
  };

  const openAddMembershipTypeModal = () => {
    const baseId = getStableTimestampId("membership-type");
    const usedIds = new Set(membershipModels.map((model) => model.id));
    const id = [
      baseId,
      ...membershipModels.map((_, index) => `${baseId}-${index + 1}`),
    ].find((candidate) => !usedIds.has(candidate)) as string;

    setMembershipTypeModalError("");
    setMembershipTypeModal({
      mode: "add",
      model: {
        id,
        code: "",
        name: "",
        description: "",
        memberTarget: 0,
        enabled: false,
        publicMembershipApplicationEnabled: false,
        position:
          Math.max(0, ...membershipModels.map((model) => model.position)) + 1,
        paymentResponsibility: "Member/applicant pays",
        thirdPartyPayerName: "",
        thirdPartyPaymentNotes: "",
        pricingModel: "fixed",
        annualPrice: "$0.00",
        monthlyPrice: "$0.00",
        includedBenefitIds: memberBenefits.map((benefit) => benefit.id),
        additionalCosts: [],
        ranges: [],
      },
    });
  };

  const addBenefit = () => {
    const nextBenefit = createNewBenefit();

    onChange({
      ...settings,
      memberBenefits: [...memberBenefits, nextBenefit],
      membershipModels: membershipModels.map((membershipModel) =>
        isTierModel(membershipModel)
          ? membershipModel
          : {
              ...membershipModel,
              includedBenefitIds: Array.from(
                new Set([...membershipModel.includedBenefitIds, nextBenefit.id])
              ),
            }
      ),
    });
  };

  const deleteBenefit = (benefitId: string) => {
    onChange({
      ...settings,
      memberBenefits: memberBenefits.filter((benefit) => benefit.id !== benefitId),
      membershipModels: membershipModels.map((membershipModel) => ({
        ...membershipModel,
        includedBenefitIds: membershipModel.includedBenefitIds.filter(
          (includedBenefitId) => includedBenefitId !== benefitId
        ),
      })),
    });
    setBenefitDeleteCandidateId(null);
  };

  const toggleTierBenefit = (
    membershipModelId: string,
    benefitId: string,
    checked: boolean
  ) => {
    updateMembershipModel(membershipModelId, (membershipModel) => ({
      ...membershipModel,
      includedBenefitIds: checked
        ? Array.from(new Set([...membershipModel.includedBenefitIds, benefitId]))
        : membershipModel.includedBenefitIds.filter((id) => id !== benefitId),
    }));
  };

  const setAllTierBenefits = (checked: boolean) => {
    const benefitIds = memberBenefits.map((benefit) => benefit.id);

    setMembershipModels((current) =>
      current.map((membershipModel) =>
        isTierModel(membershipModel)
          ? {
              ...membershipModel,
              includedBenefitIds: checked ? benefitIds : [],
            }
          : membershipModel
      )
    );
  };

  const saveAddOnModal = () => {
    if (!addOnModal) {
      return;
    }

    if (addOnModal.mode === "editFee") {
      applySharedAddOns(
        addOns.map((addOn) =>
          addOn.id === addOnModal.addOn.id
            ? {
                ...addOn,
                annualAmount: addOnModal.addOn.annualAmount,
                monthlyAmount: addOnModal.addOn.annualAmount,
              }
            : addOn
        )
      );
      setAddOnModal(null);
      return;
    }

    const nextAddOn = {
      ...addOnModal.addOn,
      code: addOnModal.addOn.code.trim(),
      name: addOnModal.addOn.name.trim(),
      accountNumber: addOnModal.addOn.accountNumber.trim(),
      description: addOnModal.addOn.description.trim(),
      monthlyAmount: addOnModal.addOn.annualAmount,
    };

    if (!nextAddOn.code || !nextAddOn.name || !nextAddOn.accountNumber) {
      return;
    }

    applySharedAddOns([...addOns, nextAddOn]);
    setAddOnModal(null);
  };

  const deleteAddOn = (addOnId: string) => {
    applySharedAddOns(addOns.filter((addOn) => addOn.id !== addOnId));
  };

  const renderMembershipGlobalSwitch = ({
    checked,
    label,
    onChange: handleChange,
    disabled = false,
    showStatus = false,
  }: {
    checked: boolean;
    label: string;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
    showStatus?: boolean;
  }) => (
    <div className="flex items-center gap-2">
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(event) => handleChange(event.target.checked)}
        sx={switchSx}
        inputProps={{ "aria-label": label }}
      />
      {showStatus && <MembershipGlobalToggleStatus enabled={checked} />}
    </div>
  );

  const renderSwitch = ({
    checked,
    label,
    onChange: handleChange,
    disabled = false,
  }: {
    checked: boolean;
    label: string;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
  }) => (
    <div className="flex items-center gap-2">
      <Switch
        checked={checked}
        disabled={disabled}
        onChange={(event) => handleChange(event.target.checked)}
        sx={switchSx}
        inputProps={{ "aria-label": label }}
      />
      <ToggleStatus enabled={checked} />
    </div>
  );

  const renderMembershipGlobalSettings = () => (
    <section className="rounded-lg border border-black bg-white px-6 py-5 shadow-none">
      <div>
        <h2 className="text-lg font-bold text-black">
          Membership Global Settings
        </h2>
        <p className="mt-1 max-w-4xl text-sm leading-6 text-black">
          Enable and set up your membership options.
        </p>
      </div>

      <div className="mt-5 space-y-5">
        <div className="grid gap-4 py-5 xl:grid-cols-[minmax(0,1fr)_minmax(420px,0.72fr)] xl:items-center">
          <div className="flex items-start gap-3">
            <MembershipGlobalStepNumber value={1} />
            <div>
              <h3 className="text-lg font-bold text-gray-950">
                Membership Payment Option
              </h3>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-700">
                Enable all membership payment options your chamber offers.
                <br className="hidden sm:block" />
                Monthly payments are made via credit card only.
              </p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 xl:gap-8">
            {paymentScheduleOptions.map((schedule) => (
              <div
                key={schedule.id}
                className="flex items-center justify-start gap-2"
              >
                <span className="text-base font-medium text-gray-700">
                  {schedule.label}
                </span>
                {renderMembershipGlobalSwitch({
                  checked: enabledPaymentSchedules[schedule.id],
                  label: `${schedule.label} payment schedule`,
                  disabled:
                    enabledPaymentSchedules[schedule.id] &&
                    enabledScheduleCount === 1,
                  onChange: (checked) =>
                    updatePaymentScheduleEnabled(schedule.id, checked),
                })}
              </div>
            ))}
          </div>
        </div>

        <div className="grid gap-4 py-5 xl:grid-cols-[minmax(0,1fr)_minmax(420px,0.72fr)] xl:items-start">
          <div className="flex items-start gap-3">
            <MembershipGlobalStepNumber value={2} />
            <div>
              <h3 className="text-lg font-bold text-gray-950">
                Auto-Renewal Settings
              </h3>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-700">
                Enable the auto-renewal option to be shown on your chamber's
                membership application form.
              </p>
              <p className="mt-5 text-sm text-gray-800">
                The auto-renewal opt-in option will be shown to members at checkout.
              </p>
            </div>
          </div>
          <div className="xl:pt-3 xl:justify-self-start">
            {renderMembershipGlobalSwitch({
              checked: globalToggleState.autoRenewal,
              label: "Auto-renewal settings",
              showStatus: true,
              onChange: (checked) =>
                updateGlobalToggleState((current) => ({
                  ...current,
                  autoRenewal: checked,
                })),
            })}
          </div>
        </div>

        <div className="grid gap-4 py-5 xl:grid-cols-[minmax(0,1fr)_minmax(420px,0.72fr)] xl:items-center">
          <div className="flex items-start gap-3">
            <MembershipGlobalStepNumber value={3} />
            <div>
              <h3 className="text-lg font-bold text-gray-950">
                Send Email For Members With Auto Renew Selected
              </h3>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-700">
                Set how many days before a member's annual membership expires to
                let them know their credit card will be charged on their renewal
                date and an invoice and payment receipt will be sent.
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center xl:justify-self-start">
            <WholeNumberInput
              value={settings.autoRenewalNoticeDays}
              onChange={(autoRenewalNoticeDays) =>
                onChange({
                  ...settings,
                  autoRenewalNoticeDays,
                })
              }
              placeholder="00"
              ariaLabel="Days before renewal date"
              className={membershipGlobalInputClassName}
            />
            <span className="text-base text-gray-700">
              days before renewal date
            </span>
          </div>
        </div>

        <div className="grid gap-4 py-5 xl:grid-cols-[minmax(0,1fr)_minmax(420px,0.72fr)] xl:items-center">
          <div className="flex items-start gap-3">
            <MembershipGlobalStepNumber value={4} />
            <div>
              <h3 className="text-lg font-bold text-gray-950">
                Send Email Reminder For Membership Renewal
              </h3>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-700">
                Set how many days before a member's membership expires to remind
                them to renew their membership.
              </p>
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 xl:justify-self-start xl:gap-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <WholeNumberInput
                value={settings.renewalReminderOneDays}
                onChange={(renewalReminderOneDays) =>
                  onChange({
                    ...settings,
                    renewalReminderOneDays,
                  })
                }
                placeholder="00"
                ariaLabel="Days before first email reminder"
                className={membershipGlobalInputClassName}
              />
              <span className="text-base text-gray-700">
                days 1st email
                <br className="hidden sm:block" />
                reminder
              </span>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <WholeNumberInput
                value={settings.renewalReminderTwoDays}
                onChange={(renewalReminderTwoDays) =>
                  onChange({
                    ...settings,
                    renewalReminderTwoDays,
                  })
                }
                placeholder="00"
                ariaLabel="Days before second email reminder"
                className={membershipGlobalInputClassName}
              />
              <span className="text-base text-gray-700">
                days 2nd email
                <br className="hidden sm:block" />
                reminder
              </span>
            </div>
          </div>
        </div>

        <div className="grid gap-4 py-5 xl:grid-cols-[minmax(0,1fr)_minmax(420px,0.72fr)] xl:items-start">
          <div className="flex items-start gap-3">
            <MembershipGlobalStepNumber value={5} />
            <div>
              <h3 className="text-lg font-bold text-gray-950">
                Membership Approval
              </h3>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-700">
                Manage how new memberships are approved.
              </p>
              <div className="mt-5 space-y-5">
                <div>
                  <p className="font-bold text-gray-950">
                    Require Chamber (Board of Directors) Approval
                  </p>
                  <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-700">
                    Enable if a new membership requires approval from a chamber
                    or Board of Directors before a membership can be accepted.
                  </p>
                </div>
                <div>
                  <p className="font-bold text-gray-950">
                    Process Payment Before Approval
                  </p>
                  <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-700">
                    Allow membership payment to be processed before chamber or
                    Board of Directors approval.
                  </p>
                  <p className="mt-4 font-bold text-gray-950">
                    *Memberships not approved will require a member refund.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="space-y-8 xl:pt-[88px] xl:justify-self-start">
            {renderMembershipGlobalSwitch({
              checked: globalToggleState.boardApproval,
              label: "Require chamber approval",
              showStatus: true,
              onChange: (checked) =>
                updateGlobalToggleState((current) => ({
                  ...current,
                  boardApproval: checked,
                })),
            })}
            {renderMembershipGlobalSwitch({
              checked:
                paymentBeforeApprovalRule === "Allow payment before approval",
              label: "Process payment before approval",
              showStatus: true,
              onChange: (checked) =>
                onChange({
                  ...settings,
                  paymentBeforeApprovalRule: checked
                    ? "Allow payment before approval"
                    : "Collect payment after approval",
                }),
            })}
          </div>
        </div>
      </div>

      <SectionSaveReset {...sectionControls} />
    </section>
  );

  const renderMembershipModelSelector = () => {
    const sizeBasedEnabled = !tierSetupActive;

    const cards = [
      {
        title: "Membership-Type (by Size)",
        enabled: sizeBasedEnabled,
        disabled: false,
        onSelect: activateSizeBasedSetup,
      },
      {
        title: "Membership-Tier",
        enabled: !sizeBasedEnabled,
        disabled: false,
        onSelect: activateTierBasedSetup,
      },
    ];

    return (
      <section className={membershipSetupCardClassName}>
        <div>
          <h2 className={membershipSetupHeaderClassName}>Membership Model</h2>
          <p className="mt-1 text-sm leading-6 text-gray-950">
            Select and setup your chamber membership structure and fees.
          </p>
        </div>

        <div
          className={`mt-12 grid gap-8 ${
            cards.length === 3 ? "lg:grid-cols-3" : "lg:grid-cols-2"
          }`}
        >
          {cards.map((card) => (
            <div
              key={card.title}
              role={card.disabled ? undefined : "button"}
              tabIndex={card.disabled ? undefined : 0}
              onClick={() => {
                if (!card.disabled) {
                  card.onSelect();
                }
              }}
              onKeyDown={(event) => {
                if (
                  !card.disabled &&
                  (event.key === "Enter" || event.key === " ")
                ) {
                  event.preventDefault();
                  card.onSelect();
                }
              }}
              className={`min-h-[108px] rounded-2xl px-5 py-5 text-center transition ${
                card.enabled
                  ? "bg-white text-cpaGold"
                  : "bg-[#ECECEC] text-[#777777]"
              } ${card.disabled ? "" : "cursor-pointer"}`}
              aria-label={card.title}
              aria-pressed={card.enabled}
            >
              <h3 className="text-lg font-bold">{card.title}</h3>
              <div className="mt-4 flex items-center justify-center gap-2">
                <Switch
                  checked={card.enabled}
                  disabled={card.disabled}
                  onChange={() => card.onSelect()}
                  sx={card.enabled ? switchSx : disabledModelSwitchSx}
                  inputProps={{ "aria-label": card.title }}
                />
                <span
                  className={`text-xs font-bold uppercase ${
                    card.enabled ? "text-emerald-600" : "text-red-500"
                  }`}
                >
                  {card.enabled ? "Enabled" : "Disabled"}
                </span>
              </div>
              {card.enabled && (
                <div className="mx-auto mt-6 h-0.5 max-w-[360px] bg-cpaGold" />
              )}
            </div>
          ))}
        </div>

        <SectionSaveReset {...sectionControls} />
      </section>
    );
  };

  const renderProratedInputs = ({
    pricingKey,
    title,
    annualPrice,
  }: {
    pricingKey: string;
    title: string;
    annualPrice: string;
  }) => (
    <div className="rounded-lg bg-white py-4">
      <h4 className="text-base font-bold leading-6 text-black">{title}</h4>
      <p className="mt-1 max-w-5xl text-sm leading-6 text-gray-950">
        Prorated memberships have been auto-generated. To add custom amounts edit
        the monthly fields. This is the amount that will show to new members on
        the chamber registration form.
      </p>
      <div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-4 md:grid-cols-3 xl:grid-cols-6">
        {defaultProrationMonths.map((month, index) => (
          <FieldLabel
            key={`${pricingKey}-${month.month}`}
            label={month.month.toUpperCase()}
          >
            <MoneyInput
              value={getProratedInputValue({
                annualPrice,
                monthIndex: index,
                savedPrice: proratedPrices[pricingKey]?.[month.month],
              })}
              onChange={(value) =>
                updateProratedPrice(pricingKey, month.month, value)
              }
              ariaLabel={`Prorated price for ${month.month}`}
            />
          </FieldLabel>
        ))}
      </div>
    </div>
  );

  const renderCompanyRangeRows = () => {
    if (!companyMembershipModel) {
      return (
        <div className="mt-8 rounded-lg bg-gray-50 p-6 text-center">
          <p className="text-base font-semibold text-gray-800">
            No company membership model is available yet.
          </p>
          <button
            type="button"
            onClick={createCompanyModelIfNeeded}
            className={`${primaryButtonClassName} mt-4`}
          >
            <AddIcon fontSize="small" />
            Create Company Membership
          </button>
        </div>
      );
    }

    return (
      <>
        <div className="mx-auto mt-12 max-w-[84rem]">
          <h3 className="text-lg font-bold text-gray-950">Company Membership</h3>
          <p className="mt-1 text-sm text-gray-950">
            For companies based on number of employees.
          </p>
        </div>

        <div className="mx-auto mt-8 max-w-[84rem] pt-7">
          <h4 className="text-base font-bold text-gray-950">
            Part-Time Employee Formula
          </h4>
          <p className="mt-2 text-sm leading-6 text-gray-950">
            Enter the number of part-time employees that will equal one
            full-time employee, if you do not use a formula just enter 1 = 1
          </p>
          <p className="mt-3 rounded-lg bg-cpaGold px-4 py-2 text-sm font-bold text-white">
            This formula will be used to calculate full-time employee range for
            determining membership fees.
          </p>
          <div className="mt-4 grid gap-4 rounded-xl bg-gray-50 p-5 md:grid-cols-[1fr_auto_1fr] md:items-center">
            <div>
              <WholeNumberInput
                value={partTimeEmployeesPerFullTime}
                onChange={(partTime) =>
                  onChange({
                    ...settings,
                    partTimeEmployeesPerFullTime: partTime,
                  })
                }
                ariaLabel="Part-time employees per full-time employee"
                className={`${compactInputClassName} w-full text-center`}
              />
              <p className="mt-2 text-center text-sm text-gray-900">
                Part-time Employees
              </p>
            </div>
            <span className="text-center text-lg font-bold text-gray-950">
              =
            </span>
            <div>
              <input
                value="1"
                readOnly
                className={`${compactInputClassName} w-full border-gray-200 bg-gray-200 text-center`}
              />
              <p className="mt-2 text-center text-sm text-gray-900">
                Full-time Employee
              </p>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-10 max-w-[84rem]">
          <h4 className="text-base font-bold text-gray-950">
            Company Membership (by Size) Fee Structure
          </h4>
          <p className="mt-2 text-sm leading-6 text-gray-950">
            Set full-time employee range and annual membership fee structure by
            simply editing the number in range fields and dollar amount fields or
            delete if not required. If additional full-time employee ranges are
            required add range and enter fields.
          </p>
        </div>

        <div className="mx-auto mt-5 max-w-[84rem] overflow-x-auto rounded-lg bg-white">
          <div className="min-w-[980px]">
            <div
              className={`grid grid-cols-[280px_180px_170px_170px_120px] ${membershipTableHeaderClassName} text-base`}
            >
              <div>Employees Range</div>
              <div>Code</div>
              <div>Annual Fee</div>
              <div>Monthly Payment</div>
              <div>Actions</div>
            </div>
            {companyMembershipModel.ranges.map((range, index, ranges) => {
              const openEndedRange = isFinalMembershipEmployeeRange(ranges, index);

              return (
                <div
                  key={range.id}
                  className={`${membershipTableRowClassName} grid-cols-[280px_180px_170px_170px_120px] items-center`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      value={
                        openEndedRange
                          ? getFinalRangeInputValue(range)
                          : range.from
                      }
                      onChange={(event) =>
                        updateRange(
                          companyMembershipModel.id,
                          range.id,
                          "from",
                          event.target.value.replace("+", "").replace(/[^0-9]/g, "")
                        )
                      }
                      className={`${compactInputClassName} w-20 text-center`}
                    />
                    {!openEndedRange && (
                      <>
                        <span className="text-base font-medium uppercase text-gray-700">
                          TO
                        </span>
                        <input
                          value={range.to}
                          onChange={(event) =>
                            updateRange(
                              companyMembershipModel.id,
                              range.id,
                              "to",
                              event.target.value.replace(/[^0-9]/g, "")
                            )
                          }
                          className={`${compactInputClassName} w-20 text-center`}
                        />
                      </>
                    )}
                  </div>
                  <input
                    value={range.code}
                    readOnly
                    aria-label={`Auto-generated code for range ${range.from || range.id}`}
                    title="Auto-generated from the company size range"
                    className={`${compactInputClassName} text-center`}
                  />
                  <MoneyInput
                    value={range.annualPrice}
                    onChange={(value) =>
                      updateRange(
                        companyMembershipModel.id,
                        range.id,
                        "annualPrice",
                        value
                      )
                    }
                    ariaLabel={`Annual fee for range ${range.code || range.from}`}
                  />
                  <TextInput
                    value={getDerivedMonthlyMembershipPrice(range.annualPrice)}
                    readOnly
                    disabled={!enabledPaymentSchedules.monthly}
                    onChange={() => undefined}
                  />
                  <button
                    type="button"
                    disabled={companyMembershipModel.ranges.length <= 1}
                    onClick={() => deleteRange(companyMembershipModel.id, range.id)}
                    className={dangerButtonClassName}
                  >
                    <DeleteIcon fontSize="small" />
                    Delete
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <button
          type="button"
          onClick={() => addRange(companyMembershipModel.id)}
          className={`${primaryButtonClassName} mx-auto mt-5 flex`}
        >
          <AddIcon fontSize="small" />
          Add Range
        </button>
      </>
    );
  };

  const renderSizeBasedSettings = () => (
    <section className={membershipSetupCardClassName}>
      <div>
        <h2 className={membershipSetupHeaderClassName}>
          Create Membership Types (by Size)
        </h2>
        <p className={membershipSetupDescriptionClassName}>
          Enable the various membership types offered by your chamber including
          setting up your employee size range and fees, which will be shown on
          your website and in your member registration form.
        </p>
      </div>

      {renderCompanyRangeRows()}

      <div className="mt-10">
        <h2 className={membershipSetupHeaderClassName}>
          Add Additional Membership Types
        </h2>
        <p className="mt-2 text-sm leading-6 text-gray-950">
          Enable the additional membership types offered by your chamber. Edit
          any of the listed membership type description and price below.{" "}
          <strong>
            If Enabled and no price is entered the fee will say “Call for
            Pricing”.
          </strong>{" "}
          You can also create a new membership type and enable it when it is
          ready to be offered.
        </p>
      </div>

      <div className="mt-6 overflow-x-auto">
        <div className="min-w-[1120px]">
          <div
            className={`grid grid-cols-[120px_minmax(300px,1.6fr)_150px_170px_210px_130px] ${membershipTableHeaderClassName} text-base`}
          >
            <div>Code</div>
            <div>Membership Type Description</div>
            <div>Member Target</div>
            <div>Membership Fee</div>
            <div>Enable Membership</div>
            <div>Actions</div>
          </div>
          {fixedMembershipModels.map((membershipModel) => (
            <div
              key={membershipModel.id}
              className={`${membershipTableRowClassName} grid-cols-[120px_minmax(300px,1.6fr)_150px_170px_210px_130px] items-center`}
            >
              <div className="text-sm font-bold text-[#55637A]">
                {membershipModel.code}
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-950">
                  {membershipModel.name}
                </h3>
                <p className="mt-1 max-w-md text-sm leading-6 text-gray-950">
                  {membershipModel.description}
                </p>
              </div>
              <div className="text-base font-bold text-gray-950">
                {membershipModel.memberTarget}
              </div>
              <div className="text-base font-bold text-gray-950">
                {parseDollars(membershipModel.annualPrice) > 0
                  ? membershipModel.annualPrice
                  : membershipModel.enabled
                  ? "Call for Pricing"
                  : membershipModel.annualPrice || "$0.00"}
              </div>
              {renderSwitch({
                checked: membershipModel.enabled,
                label: `${membershipModel.name} enabled`,
                onChange: (checked) =>
                  updateMembershipModel(membershipModel.id, (model) => ({
                    ...model,
                    enabled: checked,
                    publicMembershipApplicationEnabled: checked,
                  })),
              })}
              <button
                type="button"
                onClick={() => {
                  setMembershipTypeModalError("");
                  setMembershipTypeModal({
                    mode: "edit",
                    model: membershipModel,
                  });
                }}
                className={primaryButtonClassName}
              >
                <EditOutlinedIcon fontSize="small" />
                Edit
              </button>
            </div>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={openAddMembershipTypeModal}
        className={`${primaryButtonClassName} mt-6`}
      >
        <AddIcon fontSize="small" />
        Add Additional Membership Types
      </button>

      <SectionSaveReset {...sectionControls} />
    </section>
  );

  const renderTierSettings = () => (
    <section className={membershipSetupCardClassName}>
      <div>
        <h2 className={membershipSetupHeaderClassName}>
          Create Membership Tiers
        </h2>
        <p className={membershipSetupDescriptionClassName}>
          Edit and enable the number of membership tiers your chamber offers.
          Add a simple description for each tier and enable the tiers needed.
          <br />
          Membership Tier benefits are added below:
        </p>
      </div>

      <div className="mt-6 overflow-x-auto">
        <div className="min-w-[940px]">
          <div
            className={`grid grid-cols-[110px_90px_minmax(280px,1.5fr)_180px_230px_130px] ${membershipTableHeaderClassName} text-base`}
          >
            <div>Code</div>
            <div>Tier</div>
            <div>Membership Tier Description</div>
            <div>Membership Fee</div>
            <div>Enable Membership</div>
            <div>Actions</div>
          </div>
          {tierMembershipModels.map((tierModel, index) => (
            <div
              key={tierModel.id}
              className={`${membershipTableRowClassName} grid-cols-[110px_90px_minmax(280px,1.5fr)_180px_230px_130px] items-center`}
            >
              <div className="text-sm font-bold text-[#55637A]">
                {tierModel.code}
              </div>
              <div className="text-base font-bold text-gray-950">
                {index + 1}
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-950">
                  {tierModel.name}
                </h3>
                <p className="mt-1 text-sm leading-6 text-gray-950">
                  {tierModel.description}
                </p>
              </div>
              <div className="text-base font-bold text-gray-950">
                {tierModel.annualPrice || "$0.00"}
              </div>
              {renderSwitch({
                checked: tierModel.enabled,
                label: `${tierModel.name} enabled`,
                onChange: (checked) =>
                  updateMembershipModel(tierModel.id, (model) => ({
                    ...model,
                    enabled: checked,
                    publicMembershipApplicationEnabled: checked,
                  })),
              })}
              <button
                type="button"
                onClick={() => {
                  setMembershipTypeModalError("");
                  setMembershipTypeModal({
                    mode: "edit",
                    model: tierModel,
                  });
                }}
                className={primaryButtonClassName}
              >
                <EditOutlinedIcon fontSize="small" />
                Edit
              </button>
            </div>
          ))}
        </div>
      </div>

      <SectionSaveReset {...sectionControls} />
    </section>
  );

  const renderTierBenefits = () => {
    const showAllBenefits =
      memberBenefits.length > 0 &&
      tierMembershipModels.every((tierModel) =>
        memberBenefits.every((benefit) =>
          tierModel.includedBenefitIds.includes(benefit.id)
        )
      );

    return (
      <section className={membershipSetupCardClassName}>
        <div>
          <h2 className={membershipSetupHeaderClassName}>
            Membership Benefits for Tiered Model
          </h2>
          <p className={membershipSetupDescriptionClassName}>
            {membershipBenefitsDescription}
          </p>
        </div>

        <label className="mt-5 flex items-start gap-3 text-sm font-bold text-gray-800">
          <input
            type="checkbox"
            checked={showAllBenefits}
            onChange={(event) => setAllTierBenefits(event.target.checked)}
            className="mt-0.5 h-5 w-5 rounded border-gray-400 accent-cpaGold"
          />
          <span>
            Show all benefits listed below for all membership types. For tier
            memberships you can remove check for any tier.
          </span>
        </label>

        <div className="mt-4 overflow-x-auto bg-white">
          <div className="min-w-[920px]">
            <div
              className={`${tierBenefitsGridClassName} ${settingsTableHeaderClassName} text-center text-base`}
            >
              <div className="text-left">Benefit Description</div>
              {tierMembershipModels.map((tierModel, index) => (
                <div
                  key={`${tierModel.id}-header`}
                  className="flex items-center justify-center"
                >
                  Tier {index + 1}
                </div>
              ))}
              <div />
            </div>
            <div className="space-y-3">
              {memberBenefits.map((benefit) => (
                <div
                  key={`tier-benefit-${benefit.id}`}
                  className={`${tierBenefitsGridClassName} items-center gap-3 py-3`}
                >
                  <input
                    value={benefit.label}
                    onChange={(event) =>
                      onChange({
                        ...settings,
                        memberBenefits: memberBenefits.map((currentBenefit) =>
                          currentBenefit.id === benefit.id
                            ? {
                                ...currentBenefit,
                                label: event.target.value,
                              }
                            : currentBenefit
                        ),
                      })
                    }
                    placeholder="Enter Membership Benefit Details"
                    className={compactInputClassName}
                  />
                  {tierMembershipModels.map((tierModel) => (
                    <label
                      key={`${tierModel.id}-${benefit.id}`}
                      className="flex h-11 items-center justify-center"
                    >
                      <input
                        type="checkbox"
                        checked={tierModel.includedBenefitIds.includes(
                          benefit.id
                        )}
                        onChange={(event) =>
                          toggleTierBenefit(
                            tierModel.id,
                            benefit.id,
                            event.target.checked
                          )
                        }
                        className="h-6 w-6 rounded border-gray-400 accent-cpaGold"
                      />
                      <span className="sr-only">
                        Include {benefit.label} in {tierModel.name}
                      </span>
                    </label>
                  ))}
                  <button
                    type="button"
                    onClick={() => setBenefitDeleteCandidateId(benefit.id)}
                    className={`${dangerButtonClassName} justify-self-end`}
                  >
                    <DeleteIcon fontSize="small" />
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={addBenefit}
          className={`${primaryButtonClassName} mt-4`}
        >
          <AddIcon fontSize="small" />
          Add New Benefit
        </button>

        <SectionSaveReset {...sectionControls} />
      </section>
    );
  };

  const renderBenefits = () => (
    <section className={membershipSetupCardClassName}>
      <div>
        <h2 className={membershipSetupHeaderClassName}>
          Membership Benefits for Membership Type (by Size) Model
        </h2>
        <p className={membershipSetupDescriptionClassName}>
          {membershipBenefitsDescription}
        </p>
      </div>

      <div className="mt-6 bg-white">
        <div className={`${settingsTableHeaderClassName} text-base`}>
          Benefit Description
        </div>
        <div className="space-y-3 py-4">
          {memberBenefits.length === 0 ? (
            <p className="rounded-lg bg-gray-50 px-4 py-6 text-center text-sm font-medium text-gray-600">
              No benefits have been added.
            </p>
          ) : (
            memberBenefits.map((benefit) => (
              <div
                key={benefit.id}
                className="grid gap-3 lg:grid-cols-[1fr_auto] lg:items-center"
              >
                <input
                  value={benefit.label}
                  onChange={(event) =>
                    onChange({
                      ...settings,
                      memberBenefits: memberBenefits.map((currentBenefit) =>
                        currentBenefit.id === benefit.id
                          ? { ...currentBenefit, label: event.target.value }
                          : currentBenefit
                      ),
                    })
                  }
                  placeholder="Enter Membership Benefit Details"
                  className={compactInputClassName}
                />
                <button
                  type="button"
                  onClick={() => setBenefitDeleteCandidateId(benefit.id)}
                  className={dangerButtonClassName}
                >
                  <DeleteIcon fontSize="small" />
                  Delete
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      <button
        type="button"
        onClick={addBenefit}
        className={`${primaryButtonClassName} mt-1`}
      >
        <AddIcon fontSize="small" />
        Add New Benefit
      </button>

      <SectionSaveReset {...sectionControls} />
    </section>
  );

  const renderAddOns = () => {
    if (tierSetupActive) {
      return null;
    }

    return (
      <section className={membershipSetupCardClassName}>
        <h2 className={membershipSetupHeaderClassName}>
          Additional Membership Add on Benefits and Fees
        </h2>
        <p className="mt-2 max-w-5xl text-xs leading-5 text-gray-950">
          Create and enable additional membership add-on benefits options your
          chamber would like to offer to members at time of registration. Each
          add-on can include a name, brief description, and cost, allowing
          prospective members to select and purchase extra benefits when they
          complete the online registration form, or when your chamber registers
          a member through the Create Member in your Dashboard.
        </p>

        <div className="mt-5 overflow-x-auto">
          <div className="min-w-[980px]">
            <div
              className={`${settingsTableHeaderClassName} grid grid-cols-[80px_minmax(280px,1fr)_120px_150px_160px_100px] items-center gap-4`}
            >
              <span>Code</span>
              <span>Benefit Description</span>
              <span>Fee</span>
              <span>Account Number</span>
              <span>Enable Add-On</span>
              <span>Actions</span>
            </div>

            <div className="space-y-3">
              {addOns.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm font-semibold text-gray-500">
                  No additional membership add on benefits and fees have been
                  configured.
                </p>
              ) : (
                addOns.map((addOn) => (
                  <div
                    key={addOn.id}
                    className="grid grid-cols-[80px_minmax(280px,1fr)_120px_150px_160px_100px] items-center gap-4 px-3 py-7"
                  >
                    <span className="text-sm font-bold text-[#5B6980]">
                      {addOn.code}
                    </span>
                    <div>
                      <h3 className="text-sm font-bold leading-5 text-gray-950">
                        {addOn.name}
                      </h3>
                      <p className="mt-1 max-w-md text-xs leading-5 text-gray-950">
                        {addOn.description}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-gray-950">
                        {addOn.annualAmount}
                      </p>
                      <button
                        type="button"
                        aria-label={`Edit ${
                          addOn.code || addOn.name || "add-on"
                        } benefit fee`}
                        onClick={() =>
                          setAddOnModal({ mode: "editFee", addOn })
                        }
                        className="mt-1 inline-flex h-6 items-center justify-center gap-1 rounded-md bg-cpaGold px-3 text-xs font-semibold text-white transition hover:bg-amber-500"
                      >
                        <EditOutlinedIcon sx={{ fontSize: 13 }} />
                        Edit
                      </button>
                    </div>
                    <span className="text-sm font-bold text-gray-950">
                      {addOn.accountNumber}
                    </span>
                    {renderSwitch({
                      checked: addOn.enabled,
                      label: `${addOn.name || "Add-on"} enabled`,
                      onChange: (checked) =>
                        applySharedAddOns(
                          addOns.map((currentAddOn) =>
                            currentAddOn.id === addOn.id
                              ? { ...currentAddOn, enabled: checked }
                              : currentAddOn
                          )
                        ),
                    })}
                    <button
                      type="button"
                      onClick={() => deleteAddOn(addOn.id)}
                      className={dangerButtonClassName}
                    >
                      <DeleteIcon fontSize="small" />
                      Delete
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        <button
          type="button"
          onClick={() =>
            setAddOnModal({ mode: "add", addOn: createNewAddOn() })
          }
          className={`${primaryButtonClassName} mt-5`}
        >
          <AddIcon fontSize="small" />
          Add New Benefits and Fees
        </button>

        <SectionSaveReset {...sectionControls} />
      </section>
    );
  };

  const renderProratedPricing = () => {
    if (!enabledPaymentSchedules.annual || !globalToggleState.prorate) {
      return null;
    }

    const showCompanyRangePricing =
      !tierSetupActive &&
      !!companyMembershipModel?.enabled &&
      !!companyMembershipModel.publicMembershipApplicationEnabled &&
      companyMembershipModel.ranges.length > 0;
    const membershipModelsForProratedPricing = tierSetupActive
      ? enabledTierMembershipModels
      : enabledFixedMembershipModels;

    if (!showCompanyRangePricing && membershipModelsForProratedPricing.length === 0) {
      return null;
    }

    const readOnlyInputClassName =
      "h-11 rounded-md border border-transparent bg-[#E3E7ED] px-4 text-base text-[#2F3C52] outline-none";

    const renderAnnualFeeInput = (value: string) => (
      <input
        value={value || "$0.00"}
        readOnly
        className={`${readOnlyInputClassName} w-full max-w-[300px]`}
      />
    );

    const renderEmployeeRangeCard = (range: MembershipEmployeeRange) => {
      const rangeStart = range.from || "1";
      const rangeEnd = range.to.trim();
      const rangeLabel = rangeEnd
        ? `${rangeStart}-${rangeEnd} employees`
        : `${rangeStart}+ employees`;
      const pricingKey = `${companyMembershipModel?.id || "company"}-${
        range.id
      }`;

      return (
        <div
          key={`prorated-${range.id}`}
          className="rounded-lg bg-white"
        >
          <div className="grid gap-4 px-4 py-3 md:grid-cols-[1fr_300px]">
            <p className="text-sm font-bold text-black">Employee Range</p>
            <p className="text-sm font-bold text-black">
              Annual Membership Fee
            </p>
          </div>
          <div className="grid gap-5 px-4 py-4 md:grid-cols-[1fr_300px] md:items-start">
            <div className="flex flex-wrap items-center gap-3">
              {rangeEnd ? (
                <>
                  <input
                    value={rangeStart}
                    readOnly
                    className={`${readOnlyInputClassName} w-28 text-center`}
                  />
                  <span className="text-base font-medium uppercase text-[#3F4A5F]">
                    TO
                  </span>
                  <input
                    value={rangeEnd}
                    readOnly
                    className={`${readOnlyInputClassName} w-28 text-center`}
                  />
                </>
              ) : (
                <input
                  value={`${rangeStart}+`}
                  readOnly
                  className={`${readOnlyInputClassName} w-56 text-center`}
                />
              )}
            </div>
            {renderAnnualFeeInput(range.annualPrice)}
          </div>
          <div className="px-4 pb-6">
            {renderProratedInputs({
              pricingKey,
              title: `Prorated signup pricing: ${rangeLabel}`,
              annualPrice: range.annualPrice,
            })}
          </div>
        </div>
      );
    };

    const renderMembershipModelCard = (membershipModel: MembershipModel) => (
      <div
        key={`prorated-${membershipModel.id}`}
        className="rounded-lg bg-white"
      >
        <h3 className="px-4 py-4 text-xl font-bold leading-tight text-black">
          Prorated {membershipModel.name} Fees
        </h3>
        <div className="px-4 py-5">
          <p className="text-sm font-bold text-black">
            Annual Membership Fee
          </p>
          <div className="mt-2">
            {renderAnnualFeeInput(membershipModel.annualPrice)}
          </div>
          <div className="mt-8">
            {renderProratedInputs({
              pricingKey: membershipModel.id,
              title: `Prorated signup pricing: ${membershipModel.name}`,
              annualPrice: membershipModel.annualPrice,
            })}
          </div>
        </div>
      </div>
    );

    return (
      <section className={membershipSetupCardClassName}>
        <h2 className={membershipSetupHeaderClassName}>
          Prorated Company Membership Type Fees has Been Enabled for Your
          Chamber New Members Only
        </h2>
        <p className="mt-2 max-w-6xl text-sm leading-6 text-gray-950">
          Monthly prorated Company Membership fees have been generated below. If
          you do not want the fee to be prorated you can set a custom amount
          for each month — simply enter the amount.
        </p>

        <div className="mt-8 space-y-12">
          {showCompanyRangePricing &&
            companyMembershipModel?.ranges.map(renderEmployeeRangeCard)}
          {membershipModelsForProratedPricing.map(renderMembershipModelCard)}
        </div>

        <SectionSaveReset {...sectionControls} />
      </section>
    );
  };

  return (
    <div className="space-y-6">
      {renderMembershipGlobalSettings()}
      {renderMembershipModelSelector()}
      {tierSetupActive ? (
        <>
          {renderTierSettings()}
          {renderTierBenefits()}
        </>
      ) : (
        <>
          {renderSizeBasedSettings()}
          {renderBenefits()}
          {renderAddOns()}
        </>
      )}
      {renderProratedPricing()}

      {membershipTypeModal && (
        <div className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-black/45 p-4 pt-8">
          <div className="relative w-full max-w-4xl rounded-xl border border-gray-200 bg-white px-10 py-10 shadow-xl">
            <button
              type="button"
              onClick={() => {
                setMembershipTypeModalError("");
                setMembershipTypeModal(null);
              }}
              className="absolute right-5 top-5 flex h-8 w-8 items-center justify-center rounded-full text-xl font-bold text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
              aria-label="Close modal"
            >
              ×
            </button>

            <h2 className="text-center text-2xl font-bold text-gray-950">
              {isTierModel(membershipTypeModal.model)
                ? "Membership Tier Name and Description"
                : "Membership Type and Description"}
            </h2>

            {isTierModel(membershipTypeModal.model) ? (
              <div className="mt-10 space-y-7">
                <FieldLabel label="CODE" required>
                  <TextInput
                    value={membershipTypeModal.model.code}
                    placeholder="Enter Code"
                    onChange={(value) =>
                      setMembershipTypeModal((current) =>
                        current
                          ? {
                              ...current,
                              model: { ...current.model, code: value },
                            }
                          : current
                      )
                    }
                  />
                </FieldLabel>
                <FieldLabel label="MEMBERSHIP TIER NAME" required>
                  <TextInput
                    value={membershipTypeModal.model.name}
                    placeholder="Enter Tier Name"
                    onChange={(value) =>
                      setMembershipTypeModal((current) =>
                        current
                          ? {
                              ...current,
                              model: { ...current.model, name: value },
                            }
                          : current
                      )
                    }
                  />
                </FieldLabel>
                <FieldLabel label="MEMBERSHIP TIER DESCRIPTION">
                  <textarea
                    value={membershipTypeModal.model.description}
                    placeholder="Enter Tier Description"
                    onChange={(event) =>
                      setMembershipTypeModal((current) =>
                        current
                          ? {
                              ...current,
                              model: {
                                ...current.model,
                                description: event.target.value,
                              },
                            }
                          : current
                      )
                    }
                    className={textareaClassName}
                  />
                </FieldLabel>
                <FieldLabel label="MEMBERSHIP TIER FEE" required>
                  <MoneyInput
                    value={membershipTypeModal.model.annualPrice}
                    placeholder="$0.00"
                    onChange={(value) =>
                      setMembershipTypeModal((current) =>
                        current
                          ? {
                              ...current,
                              model: {
                                ...current.model,
                                annualPrice: value,
                                monthlyPrice:
                                  getDerivedMonthlyMembershipPrice(value),
                              },
                            }
                          : current
                      )
                    }
                  />
                </FieldLabel>
              </div>
            ) : (
              <div className="mt-10 space-y-6">
                <FieldLabel label="CODE" required>
                  <TextInput
                    value={membershipTypeModal.model.code}
                    placeholder="Enter Membership Type Code"
                    maxLength={20}
                    onChange={(value) => {
                      setMembershipTypeModalError("");
                      setMembershipTypeModal((current) =>
                        current
                          ? {
                              ...current,
                              model: { ...current.model, code: value },
                            }
                          : current
                      );
                    }}
                  />
                </FieldLabel>
                <FieldLabel label="MEMBERSHIP TYPE NAME" required>
                  <input
                    value={membershipTypeModal.model.name}
                    maxLength={100}
                    placeholder="Enter Membership Type Name"
                    onChange={(event) =>
                      setMembershipTypeModal((current) =>
                        current
                          ? {
                              ...current,
                              model: {
                                ...current.model,
                                name: event.target.value,
                              },
                            }
                          : current
                      )
                    }
                    className={`${compactInputClassName} w-full`}
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    {membershipTypeModal.model.name.length}/100 Characters
                  </p>
                </FieldLabel>
                <FieldLabel label="MEMBERSHIP TYPE DESCRIPTION" required>
                  <textarea
                    value={membershipTypeModal.model.description}
                    maxLength={200}
                    placeholder="Enter Membership Type Description"
                    onChange={(event) =>
                      setMembershipTypeModal((current) =>
                        current
                          ? {
                              ...current,
                              model: {
                                ...current.model,
                                description: event.target.value,
                              },
                            }
                          : current
                      )
                    }
                    className={textareaClassName}
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    {membershipTypeModal.model.description.length}/200
                    Characters
                  </p>
                </FieldLabel>
                <FieldLabel label="MEMBER TARGET">
                  <p className="-mt-1 mb-2 text-sm leading-5 text-gray-950">
                    Set the target number of members for this membership type.
                    This goal is used for tracking membership growth.
                  </p>
                  <WholeNumberInput
                    value={String(membershipTypeModal.model.memberTarget)}
                    placeholder="00"
                    ariaLabel="Membership type member target"
                    onChange={(value) =>
                      setMembershipTypeModal((current) =>
                        current
                          ? {
                              ...current,
                              model: {
                                ...current.model,
                                memberTarget: Number(value) || 0,
                              },
                            }
                          : current
                      )
                    }
                  />
                </FieldLabel>
                <FieldLabel label="MEMBERSHIP TYPE FEE" required>
                  <p className="-mt-1 mb-2 text-sm leading-5 text-gray-950">
                    If no price is entered, the box will display{" "}
                    <strong>“Call for Pricing”.</strong>
                  </p>
                  <MoneyInput
                    value={membershipTypeModal.model.annualPrice}
                    placeholder="$0.00"
                    onChange={(value) =>
                      setMembershipTypeModal((current) =>
                        current
                          ? {
                              ...current,
                              model: {
                                ...current.model,
                                annualPrice: value,
                                monthlyPrice:
                                  getDerivedMonthlyMembershipPrice(value),
                              },
                            }
                          : current
                      )
                    }
                  />
                </FieldLabel>
              </div>
            )}

            {membershipTypeModalError && (
              <p className="mt-6 text-center text-sm font-semibold text-red-600">
                {membershipTypeModalError}
              </p>
            )}

            <div className="mt-10 flex justify-center">
              <button
                type="button"
                onClick={saveMembershipTypeModal}
                className={`${primaryButtonClassName} min-w-[160px]`}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {benefitDeleteCandidateId && (
        <div className="fixed inset-0 z-[10000] flex items-start justify-center bg-black/45 p-4 pt-10">
          <div className="w-full max-w-2xl">
            <div className="relative rounded-xl bg-white px-10 py-12 shadow-xl">
              <button
                type="button"
                onClick={() => setBenefitDeleteCandidateId(null)}
                className="absolute right-5 top-5 flex h-10 w-10 items-center justify-center rounded-full bg-red-500 text-2xl font-bold leading-none text-white transition hover:bg-red-600"
                aria-label="Close delete benefit confirmation"
              >
                ×
              </button>
              <div className="mx-auto flex h-28 w-28 items-center justify-center rounded-full bg-red-500 text-white">
                <DeleteIcon sx={{ fontSize: 58 }} />
              </div>
              <h2 className="mx-auto mt-10 max-w-lg text-center text-2xl font-bold leading-snug text-gray-950">
                Are you sure you want to delete the benefit?
              </h2>
              <div className="mt-10 flex flex-col justify-center gap-5 sm:flex-row">
                <button
                  type="button"
                  onClick={() => deleteBenefit(benefitDeleteCandidateId)}
                  className="inline-flex h-14 min-w-[200px] items-center justify-center gap-3 rounded-lg bg-red-500 px-6 text-base font-bold uppercase text-white shadow-md transition hover:bg-red-600"
                >
                  <DeleteIcon fontSize="small" />
                  DELETE
                </button>
                <button
                  type="button"
                  onClick={() => setBenefitDeleteCandidateId(null)}
                  className="inline-flex h-14 min-w-[200px] items-center justify-center rounded-lg bg-gray-950 px-6 text-base font-bold uppercase text-white shadow-md transition hover:bg-gray-800"
                >
                  CANCEL
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {addOnModal?.mode === "add" && (
        <div className="fixed inset-0 z-[9999] flex items-start justify-center overflow-y-auto bg-black/45 p-4 pt-8">
          <div className="w-full max-w-4xl">
            <div className="relative rounded-xl bg-white px-10 py-10 shadow-xl">
              <button
                type="button"
                onClick={() => setAddOnModal(null)}
                className="absolute right-5 top-5 flex h-8 w-8 items-center justify-center rounded-full text-xl font-bold text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
                aria-label="Close add benefit modal"
              >
                ×
              </button>
              <h2 className="text-center text-2xl font-bold text-gray-950">
                Add Benefit
              </h2>
              <p className="mt-5 text-center text-sm leading-6 text-gray-950">
                Fill in the form below to add a new benefit to the existing
                membership model type.
              </p>

              <div className="mt-8 space-y-6">
                <FieldLabel label="CODE" required>
                  <TextInput
                    value={addOnModal.addOn.code}
                    placeholder="Enter Code"
                    onChange={(value) =>
                      setAddOnModal((current) =>
                        current
                          ? {
                              ...current,
                              addOn: { ...current.addOn, code: value },
                            }
                          : current
                      )
                    }
                  />
                </FieldLabel>
                <FieldLabel label="BENEFIT NAME" required>
                  <TextInput
                    value={addOnModal.addOn.name}
                    maxLength={100}
                    placeholder="Enter Additional Benefit Name"
                    onChange={(value) =>
                      setAddOnModal((current) =>
                        current
                          ? {
                              ...current,
                              addOn: { ...current.addOn, name: value },
                            }
                          : current
                      )
                    }
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    {addOnModal.addOn.name.length}/100 characters
                  </p>
                </FieldLabel>
                <FieldLabel label="BENEFIT DESCRIPTION">
                  <textarea
                    value={addOnModal.addOn.description}
                    maxLength={250}
                    placeholder="Enter Additional Benefit Description"
                    onChange={(event) =>
                      setAddOnModal((current) =>
                        current
                          ? {
                              ...current,
                              addOn: {
                                ...current.addOn,
                                description: event.target.value,
                              },
                            }
                          : current
                      )
                    }
                    className={textareaClassName}
                  />
                  <p className="mt-2 text-sm text-gray-500">
                    {addOnModal.addOn.description.length}/250 characters
                  </p>
                </FieldLabel>
                <FieldLabel label="BENEFIT FEE" required>
                  <MoneyInput
                    value={addOnModal.addOn.annualAmount}
                    placeholder="$0.00"
                    onChange={(value) =>
                      setAddOnModal((current) =>
                        current
                          ? {
                              ...current,
                              addOn: {
                                ...current.addOn,
                                annualAmount: value,
                                monthlyAmount: value,
                              },
                            }
                          : current
                      )
                    }
                  />
                </FieldLabel>
                <FieldLabel label="ACCOUNT NUMBER" required>
                  <select
                    value={addOnModal.addOn.accountNumber}
                    onChange={(event) =>
                      setAddOnModal((current) =>
                        current
                          ? {
                              ...current,
                              addOn: {
                                ...current.addOn,
                                accountNumber: event.target.value,
                              },
                            }
                          : current
                      )
                    }
                    className={`${compactInputClassName} w-full`}
                  >
                    <option value="" disabled>
                      Select Account Number
                    </option>
                    {accountNumberOptions.map((accountNumber) => (
                      <option key={accountNumber} value={accountNumber}>
                        {accountNumber}
                      </option>
                    ))}
                  </select>
                </FieldLabel>
              </div>

              <div className="mt-10 flex justify-center">
                <button
                  type="button"
                  onClick={saveAddOnModal}
                  className={`${primaryButtonClassName} min-w-[150px]`}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {addOnModal?.mode === "editFee" && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/45 p-4">
          <div className="relative w-full max-w-md rounded-xl bg-white px-10 py-10 shadow-xl">
            <button
              type="button"
              onClick={() => setAddOnModal(null)}
              className="absolute right-5 top-5 flex h-8 w-8 items-center justify-center rounded-full text-xl font-bold text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
              aria-label="Close edit benefit fee modal"
            >
              ×
            </button>
            <h2 className="text-center text-xl font-bold text-gray-950">
              Edit Benefit Fee
            </h2>
            <div className="mt-8">
              <MoneyInput
                value={addOnModal.addOn.annualAmount}
                placeholder="$0.00"
                onChange={(value) =>
                  setAddOnModal((current) =>
                    current
                      ? {
                          ...current,
                          addOn: {
                            ...current.addOn,
                            annualAmount: value,
                          },
                        }
                      : current
                  )
                }
              />
            </div>
            <div className="mt-8 flex justify-center">
              <button
                type="button"
                onClick={saveAddOnModal}
                className={`${primaryButtonClassName} min-w-[145px]`}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MembershipTab;
